import { useEffect, useState } from 'react';
import { CalendarOff, Plus, Trash2 } from 'lucide-react';
import { useAuthedApi } from '../../lib/useAuthedApi';

type Block = {
  id: string;
  start_at: string;
  end_at: string;
  reason: string | null;
};

type FormState = {
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  reason: string;
};

const EMPTY_FORM: FormState = {
  start_date: '',
  start_time: '09:00',
  end_date: '',
  end_time: '17:00',
  reason: '',
};

/**
 * Render a stored UTC timestamp in the viewer's local timezone as a
 * human-readable stamp, e.g. "Wed, Jun 25, 2026, 2:30 PM" (no seconds, named
 * weekday + month). Replaces the raw toLocaleString() default.
 */
function fmtStamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Combine a YYYY-MM-DD date + HH:MM time as a local Date, then return UTC ISO.
 * Returns null if either part is missing or unparseable.
 */
function toIsoUtc(date: string, time: string): string | null {
  if (!date || !time) return null;
  const local = new Date(`${date}T${time}`);
  if (Number.isNaN(local.getTime())) return null;
  return local.toISOString();
}

export default function TimeOff() {
  const apiFn = useAuthedApi();
  const [staffId, setStaffId] = useState<string | null>(null);
  const [items, setItems] = useState<Block[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  async function load(id: string) {
    setItems(await apiFn<Block[]>(`/staff/${id}/time-off`));
  }

  useEffect(() => {
    apiFn<{ id: string }>('/staff/me')
      .then((me) => {
        setStaffId(me.id);
        return load(me.id);
      })
      .catch((e) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function add() {
    if (!staffId) return;
    const startIso = toIsoUtc(form.start_date, form.start_time);
    const endIso = toIsoUtc(form.end_date, form.end_time);
    if (!startIso || !endIso) {
      setErr('Both a start and end date/time are required.');
      return;
    }
    if (new Date(startIso) >= new Date(endIso)) {
      setErr('End must be after start.');
      return;
    }

    setErr(null);
    setSaving(true);
    try {
      await apiFn(`/staff/${staffId}/time-off`, {
        body: {
          start_at: startIso,
          end_at: endIso,
          reason: form.reason || null,
        },
      });
      setForm(EMPTY_FORM);
      await load(staffId);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!staffId) return;
    if (!confirm('Delete this time-off block?')) return;
    await apiFn(`/staff/${staffId}/time-off/${id}`, { method: 'DELETE' });
    await load(staffId);
  }

  if (!staffId) return err ? <p className="err">{err}</p> : <p className="muted">Loading…</p>;

  return (
    <div>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>
          <CalendarOff size={16} /> Add time off
        </h2>
        <p className="muted">
          Any bookings that fall inside this range will be blocked. Times are in your local
          timezone.
        </p>

        <div className="time-off-row">
          <div className="time-off-field">
            <label>
              <span>Start date</span>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    start_date: e.target.value,
                    // Mirror to end_date the first time the user picks a start, so
                    // single-day blocks only need one date pick.
                    end_date: f.end_date || e.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="time-off-field">
            <label>
              <span>Start time</span>
              <input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
              />
            </label>
          </div>
        </div>

        <div className="time-off-row">
          <div className="time-off-field">
            <label>
              <span>End date</span>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
              />
            </label>
          </div>
          <div className="time-off-field">
            <label>
              <span>End time</span>
              <input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
              />
            </label>
          </div>
        </div>

        <label>
          <span>Reason (optional)</span>
          <input
            value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            placeholder="e.g. Vacation, sick day"
          />
        </label>

        {err && <p className="err">{err}</p>}

        <button className="primary" onClick={add} disabled={saving}>
          <Plus size={14} /> {saving ? 'Saving…' : 'Add time off'}
        </button>
      </div>

      <table>
        <thead>
          <tr>
            <th>Start</th>
            <th>End</th>
            <th>Reason</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={4} className="muted">No time off scheduled.</td>
            </tr>
          ) : (
            items.map((b) => (
              <tr key={b.id}>
                <td>{fmtStamp(b.start_at)}</td>
                <td>{fmtStamp(b.end_at)}</td>
                <td>{b.reason ?? '—'}</td>
                <td>
                  <button className="danger" onClick={() => remove(b.id)}>
                    <Trash2 size={14} /> Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
